import {objectFromEntries} from '@benchling/js-utils';
import {uniq} from 'underscore';

import {assert} from 'Asserts';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {bulkFetchMeasurementUnits, bulkFetchUnitsBySymbol} from 'inventory/InventoryUnitUtil';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  UnitsCustomResolutionQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {EMPTY_TYPED_VALUE} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolvedValueInfo,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

/**
 * Units resolved through the async custom resolution system.
 *
 * Table evaluation must never read units straight out of the Apollo cache: a cache miss there is
 * indistinguishable from "this unit does not exist", so callers either crash or silently skip
 * validation. Routing unit loads through `CustomResolutionQueryType.UNITS` instead turns a miss into
 * a `LoadingError`, which the Resolver retries and a later evaluation pass resolves.
 *
 * A `UNITS` query carries unit ids, unit symbols, or both. The resolution result is an array of JSON
 * typed values ordered as `[...unitIds, ...unitSymbols]`; entries that the backend did not return are
 * `null`.
 */

function makeUnitsQuery({
  unitIds,
  unitSymbols,
}: {
  unitIds?: Array<string>;
  unitSymbols?: Array<string>;
}): UnitsCustomResolutionQuery {
  return {
    type: ResolutionQueryType.CUSTOM,
    customResolutionQueryType: CustomResolutionQueryType.UNITS,
    unitIds: unitIds ?? [],
    ...(unitSymbols != null && {unitSymbols}),
  };
}

/** Returns loaded units as an array of MeasurementUnits, one per requested unit id then symbol. */
export async function bulkResolveUnitsToServerResolutionResult(
  unitsQueries: Array<UnitsCustomResolutionQuery>
): Promise<Array<ServerResolutionResult>> {
  const uniqueUnitIds = uniq(unitsQueries.flatMap((query) => query.unitIds));
  const uniqueUnitSymbols = uniq(unitsQueries.flatMap((query) => query.unitSymbols ?? []));
  const [units, unitsBySymbol]: [Array<MeasurementUnit>, {[symbol: string]: MeasurementUnit}] =
    await Promise.all([
      uniqueUnitIds.length > 0 ? bulkFetchMeasurementUnits(uniqueUnitIds) : [],
      uniqueUnitSymbols.length > 0 ? bulkFetchUnitsBySymbol(uniqueUnitSymbols) : {},
    ]);
  const unitsById = objectFromEntries(units.map((unit) => [unit.id, unit]));
  return unitsQueries.map((query) =>
    makeCustomResolutionResultFromTypedValue({
      type: BaseTypedValueType.ARRAY,
      rawValue: [
        ...query.unitIds.map((unitId) => ({
          type: BaseTypedValueType.JSON as const,
          jsonValue: unitsById[unitId],
        })),
        ...(query.unitSymbols ?? []).map((symbol) => ({
          type: BaseTypedValueType.JSON as const,
          jsonValue: unitsBySymbol[symbol] ?? null,
        })),
      ],
    })
  );
}

function resolveUnitsForQuery(
  query: UnitsCustomResolutionQuery,
  resolveFromResolutionQuery: ResolveQueryFn
): {units?: Array<MeasurementUnit | null>; error?: ResolvedValueInfo['error']} {
  const {resolvedValue, error} = resolveFromResolutionQuery(
    EMPTY_TYPED_VALUE,
    query,
    makeTypedValueFromCustomResolvedValue
  );

  // If we have an error from trying to fetch the units, return the error and don't try to solve. This is
  // usually just the loading "error" while we are still fetching the units.
  if (error != null) {
    return {error};
  }
  assert(resolvedValue.type === BaseTypedValueType.ARRAY, 'Resolved value must be an array', {
    resolvedValue,
  });

  return {
    units: resolvedValue.rawValue.map((unit) => {
      assert(unit.type === BaseTypedValueType.JSON, 'Unit must be a JSON value', {unit});
      return unit.jsonValue ?? null;
    }),
  };
}

/** Resolve the given unit ids into MeasurementUnits, in the order they were requested. */
export function resolveUnitsFromUnitIds(
  unitIds: Array<string>,
  resolveFromResolutionQuery: ResolveQueryFn
): {units?: Array<MeasurementUnit>; error?: ResolvedValueInfo['error']} {
  const {units, error} = resolveUnitsForQuery(makeUnitsQuery({unitIds}), resolveFromResolutionQuery);
  return error != null ? {error} : {units: units as Array<MeasurementUnit>};
}

/**
 * Resolve the given unit symbols into MeasurementUnits, keyed by symbol.
 *
 * Symbols the backend does not recognize are absent from the returned mapping rather than being
 * reported as an error, so callers must handle a missing symbol.
 */
export function resolveUnitsBySymbols(
  symbols: Array<string>,
  resolveFromResolutionQuery: ResolveQueryFn
): {unitsBySymbol?: {[symbol: string]: MeasurementUnit}; error?: ResolvedValueInfo['error']} {
  const requestedSymbols = uniq(symbols.filter((symbol) => symbol !== ''));
  if (requestedSymbols.length === 0) {
    return {unitsBySymbol: {}};
  }
  const {units, error} = resolveUnitsForQuery(
    makeUnitsQuery({unitSymbols: requestedSymbols}),
    resolveFromResolutionQuery
  );
  if (error != null) {
    return {error};
  }
  return {
    unitsBySymbol: objectFromEntries(
      requestedSymbols.flatMap((symbol, index) => {
        const unit = units![index];
        return unit != null ? [[symbol, unit] as [string, MeasurementUnit]] : [];
      })
    ),
  };
}
